"use client";

import React, { useState } from "react";
import Link from "next/link";
import { motion } from "motion/react";
import { useForm } from "react-hook-form";
import { customZodResolver } from "@/lib/schemas";
import { applicationSchema, ApplicationFormValues } from "@/lib/schemas";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import { ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import { container, item } from "@/lib/framer";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const API_URL = "/api";

export default function OnlineAdmission() {
  const [step, setStep] = useState(1);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [applicationId, setApplicationId] = useState("");

  const form = useForm<ApplicationFormValues>({
    mode: "onBlur",
    reValidateMode: "onChange",
    resolver: customZodResolver(applicationSchema as any),
    defaultValues: {
      firstName: "",
      lastName: "",
      dob: "",
      gender: "male",
      address: "",
      city: "",
      district: "",
      nationality: "Nepali",
      fatherName: "",
      fatherOccupation: "",
      fatherPhone: "",
      motherName: "",
      motherOccupation: "",
      motherPhone: "",
      email: "",
      emergencyContactName: "",
      emergencyPhone: "",
      relationship: "",
      gradeApplying: "nursery",
      previousSchool: "",
      lastGradeCompleted: "",
      academicYear: "2083",
      hasSiblings: "no",
      healthInfo: "",
      additionalInfo: "",
      terms: false,
    },
  });

  // Validate fields for each step
  const validateStep = async (stepNumber: number) => {
    const fieldsToValidate = {
      1: [
        "firstName",
        "lastName",
        "dob",
        "gender",
        "address",
        "city",
        "district",
        "nationality",
      ],
      2: [
        "fatherName",
        "fatherPhone",
        "motherName",
        "motherPhone",
        "email",
        "emergencyContactName",
        "emergencyPhone",
        "relationship",
      ],
      3: ["gradeApplying", "academicYear", "hasSiblings", "terms"],
    };

    const fields =
      fieldsToValidate[stepNumber as keyof typeof fieldsToValidate];
    const isStepValid = await form.trigger(fields as any);

    if (!isStepValid) {
      // Show validation errors
      const errors = form.formState.errors;
      const errorMessages = fields
        .filter((field) => errors[field as keyof typeof errors])
        .map((field) => {
          const error = errors[field as keyof typeof errors];
          const fieldName = field.replace(/([A-Z])/g, " $1").toLowerCase();
          return `${fieldName}: ${error?.message}`;
        });

      if (errorMessages.length > 0) {
        toast(
          `Please fix these errors: ${errorMessages.slice(0, 3).join(", ")}${errorMessages.length > 3 ? "..." : ""}`,
        );
      }
      return false;
    }
    return true;
  };

  const handleNextStep = async (data: ApplicationFormValues) => {
    try {
      if (step < 3) {
        const isValid = await validateStep(step);
        if (isValid) {
          setStep(step + 1);
          window.scrollTo(0, 0);
        }
      } else {
        const isValid = await validateStep(3);
        if (!isValid) return;

        try {
          setLoading(true);
          const response = await fetch(`${API_URL}/contact/admission`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
          });
          if (!response.ok) {
            throw new Error("Failed to submit application");
          }
          const result = await response.json();
          toast("Application Submitted Successfully");
          setFormSubmitted(true);
          setApplicationId(
            result.applicationId || result.application?.id || result.id,
          );
          setLoading(false);
        } catch (error) {
          console.error("Error submitting application:", error);
          toast("Failed to submit application");
          setLoading(false);
        }
      }
    } catch (error) {
      console.error("Error in form submission:", error);
    }
  };

  const handlePrevStep = () => {
    if (step > 1) {
      setStep(step - 1);
      window.scrollTo(0, 0);
    }
  };

  if (formSubmitted) {
    return (
      <div className="min-h-screen bg-muted/30 flex flex-col">
        <main className="flex-1 container mx-auto py-12 flex items-center justify-center">
          <motion.div
            className="max-w-md w-full text-center"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", duration: 0.5 }}
          >
            <div className="mb-6 flex justify-center">
              <div className="rounded-full bg-primary/10 p-3">
                <CheckCircle2 className="h-12 w-12 text-primary" />
              </div>
            </div>
            <h1 className="text-2xl font-bold mb-4">
              Application Submitted Successfully!
            </h1>
            <p className="text-muted-foreground mb-8">
              Thank you for applying to Shree Janakalyan Boarding School. We
              have received your application and will contact you shortly.
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              Your application reference number:{" "}
              <span className="font-mono font-bold">SJBS-{applicationId}</span>
            </p>
            <div className="flex flex-col gap-4">
              <Button asChild>
                <Link href="/">Return to Homepage</Link>
              </Button>
              {/* <Button variant="outline" asChild>
                <Link href="#" onClick={(e) => e.preventDefault()}>
                  Download Application Copy
                </Link>
              </Button> */}
            </div>
          </motion.div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 flex flex-col">
      <main className="flex-1 container mx-auto py-12">
        <div className="mb-8">
          <Link
            href="/"
            className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Link>
        </div>

        <div className="grid gap-8 md:grid-cols-3">
          <div className="md:col-span-1">
            <div className="sticky top-24">
              <Card>
                <CardHeader>
                  <CardTitle>Online Admission</CardTitle>
                  <CardDescription>
                    Complete the form to apply for admission
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div
                        className={`rounded-full h-8 w-8 flex items-center justify-center ${step >= 1 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
                      >
                        1
                      </div>
                      <div>
                        <p
                          className={`font-medium ${step >= 1 ? "text-primary" : "text-muted-foreground"}`}
                        >
                          Student Information
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div
                        className={`rounded-full h-8 w-8 flex items-center justify-center ${step >= 2 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
                      >
                        2
                      </div>
                      <div>
                        <p
                          className={`font-medium ${step >= 2 ? "text-primary" : "text-muted-foreground"}`}
                        >
                          Parent/Guardian Details
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div
                        className={`rounded-full h-8 w-8 flex items-center justify-center ${step >= 3 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
                      >
                        3
                      </div>
                      <div>
                        <p
                          className={`font-medium ${step >= 3 ? "text-primary" : "text-muted-foreground"}`}
                        >
                          Academic Information
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex flex-col items-start">
                  <p className="text-sm text-muted-foreground mb-4">
                    Need help with your application? Contact our admissions
                    office:
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">Phone:</span>{" "}
                    <span className="text-primary ">
                      +977 9808718289, +977 98046042783
                    </span>
                  </p>
                  <div className="text-sm flex-col flex lg:flex-row gap-2">
                    <p className="font-medium">Email:</p>{" "}
                    <p className="text-primary ">
                      <a href="mailto:admin@janakalyanboardingschool.edu.np">
                        admin@janakalyanboardingschool.edu.np
                      </a>
                      ,{" "}
                      <a href="mailto:janakalyanboardingschool@gmail.com">
                        janakalyanboardingschool@gmail.com
                      </a>
                    </p>
                  </div>
                </CardFooter>
              </Card>
            </div>
          </div>

          <div className="md:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>
                  {step === 1 && "Student Information"}
                  {step === 2 && "Parent/Guardian Details"}
                  {step === 3 && "Academic Information"}
                </CardTitle>
                <CardDescription>
                  {step === 1 &&
                    "Please provide the student's personal details"}
                  {step === 2 &&
                    "Please provide contact information for parents/guardians"}
                  {step === 3 &&
                    "Please provide previous academic details and preferences"}
                </CardDescription>

                {/* Progress Indicator */}
                <div className="flex items-center justify-center space-x-2 pt-4">
                  {[1, 2, 3].map((stepNumber, index) => (
                    <div key={stepNumber} className="flex items-center">
                      <div
                        className={`flex items-center justify-center w-8 h-8 rounded-full border-2 text-sm font-medium transition-colors ${
                          stepNumber === step
                            ? "border-primary bg-primary text-primary-foreground"
                            : stepNumber < step
                              ? "border-green-500 bg-green-500 text-white"
                              : "border-muted-foreground text-muted-foreground bg-background"
                        }`}
                      >
                        {stepNumber < step ? (
                          <CheckCircle2 className="w-4 h-4" />
                        ) : (
                          stepNumber
                        )}
                      </div>
                      <div className="ml-2 text-xs text-muted-foreground">
                        {stepNumber === 1 && "Student"}
                        {stepNumber === 2 && "Parent"}
                        {stepNumber === 3 && "Academic"}
                      </div>
                      {index < 2 && (
                        <div
                          className={`w-6 sm:w-12 h-0.5 mx-1 sm:mx-3 transition-colors ${
                            stepNumber < step
                              ? "bg-green-500"
                              : "bg-muted-foreground/30"
                          }`}
                        ></div>
                      )}
                    </div>
                  ))}
                </div>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(handleNextStep)}
                    className="space-y-4"
                  >
                    {step === 1 && (
                      <motion.div
                        className="space-y-4"
                        initial="hidden"
                        animate="show"
                        variants={container}
                      >
                        <motion.div
                          className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                          variants={item}
                        >
                          <FormField
                            control={form.control}
                            name="firstName"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>First Name</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder="Enter first name"
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage className="text-destructive" />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="lastName"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Last Name</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder="Enter last name"
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage className="text-destructive" />
                              </FormItem>
                            )}
                          />
                        </motion.div>

                        <FormField
                          control={form.control}
                          name="dob"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Date of Birth</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Enter date of birth"
                                  type="date"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage className="text-destructive" />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="gender"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Gender</FormLabel>
                              <RadioGroup
                                defaultValue={field.value}
                                onValueChange={field.onChange}
                                className="flex flex-wrap gap-4"
                              >
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="male" id="male" />
                                  <Label htmlFor="male">Male</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="female" id="female" />
                                  <Label htmlFor="female">Female</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="other" id="other" />
                                  <Label htmlFor="other">Other</Label>
                                </div>
                              </RadioGroup>
                              <FormMessage className="text-destructive" />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="address"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Address</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Enter home address"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage className="text-destructive" />
                            </FormItem>
                          )}
                        />

                        <motion.div
                          className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                          variants={item}
                        >
                          <FormField
                            control={form.control}
                            name="city"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>City</FormLabel>
                                <FormControl>
                                  <Input placeholder="Enter city" {...field} />
                                </FormControl>
                                <FormMessage className="text-destructive" />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="district"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>District</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder="Enter district"
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage className="text-destructive" />
                              </FormItem>
                            )}
                          />
                        </motion.div>

                        <FormField
                          control={form.control}
                          name="nationality"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Nationality</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Enter nationality"
                                  defaultValue="Nepali"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage className="text-destructive" />
                            </FormItem>
                          )}
                        />
                      </motion.div>
                    )}

                    {step === 2 && (
                      <motion.div
                        className="space-y-4"
                        initial="hidden"
                        animate="show"
                        variants={container}
                      >
                        <FormField
                          control={form.control}
                          name="fatherName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Father&apos;s Name</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Enter father's name"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage className="text-destructive" />
                            </FormItem>
                          )}
                        />

                        <motion.div
                          className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                          variants={item}
                        >
                          <FormField
                            control={form.control}
                            name="fatherOccupation"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Father&apos;s Occupation</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder="Enter occupation"
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage className="text-destructive" />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="fatherPhone"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Father&apos;s Phone</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder="Enter phone number"
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage className="text-destructive" />
                              </FormItem>
                            )}
                          />
                        </motion.div>

                        <FormField
                          control={form.control}
                          name="motherName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Mother&apos;s Name</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Enter mother's name"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage className="text-destructive" />
                            </FormItem>
                          )}
                        />

                        <motion.div
                          className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                          variants={item}
                        >
                          <FormField
                            control={form.control}
                            name="motherOccupation"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Mother&apos;s Occupation</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder="Enter occupation"
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage className="text-destructive" />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="motherPhone"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Mother&apos;s Phone</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder="Enter phone number"
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage className="text-destructive" />
                              </FormItem>
                            )}
                          />
                        </motion.div>

                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Email Address</FormLabel>
                              <FormControl>
                                <Input
                                  type="email"
                                  placeholder="Enter email address"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage className="text-destructive" />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="emergencyContactName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Emergency Contact Name</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Enter emergency contact name"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage className="text-destructive" />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="emergencyPhone"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Emergency Contact Phone</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Enter emergency contact phone"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage className="text-destructive" />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="relationship"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Relationship to Student</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="E.g., Parent, Grandparent, Uncle, etc."
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage className="text-destructive" />
                            </FormItem>
                          )}
                        />
                      </motion.div>
                    )}

                    {step === 3 && (
                      <motion.div
                        className="space-y-4"
                        initial="hidden"
                        animate="show"
                        variants={container}
                      >
                        <FormField
                          control={form.control}
                          name="gradeApplying"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Grade Applying For</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select grade" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="nursery">
                                    Nursery
                                  </SelectItem>
                                  <SelectItem value="lkg">
                                    LKG (Lower Kindergarten)
                                  </SelectItem>
                                  <SelectItem value="ukg">
                                    UKG (Upper Kindergarten)
                                  </SelectItem>
                                  <SelectItem value="class-1">
                                    Class 1
                                  </SelectItem>
                                  <SelectItem value="class-2">
                                    Class 2
                                  </SelectItem>
                                  <SelectItem value="class-3">
                                    Class 3
                                  </SelectItem>
                                  <SelectItem value="class-4">
                                    Class 4
                                  </SelectItem>
                                  <SelectItem value="class-5">
                                    Class 5
                                  </SelectItem>
                                  <SelectItem value="class-6">
                                    Class 6
                                  </SelectItem>
                                  <SelectItem value="class-7">
                                    Class 7
                                  </SelectItem>
                                  <SelectItem value="class-8">
                                    Class 8
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage className="text-destructive" />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="previousSchool"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Previous School (if any)</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Enter previous school name"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage className="text-destructive" />
                            </FormItem>
                          )}
                        />

                        <motion.div
                          className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                          variants={item}
                        >
                          <FormField
                            control={form.control}
                            name="lastGradeCompleted"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Last Grade Completed</FormLabel>
                                <Select
                                  onValueChange={field.onChange}
                                  defaultValue={field.value}
                                >
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select grade" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    <SelectItem value="none">None</SelectItem>
                                    <SelectItem value="kg">
                                      Kindergarten
                                    </SelectItem>
                                    <SelectItem value="1">Grade 1</SelectItem>
                                    <SelectItem value="2">Grade 2</SelectItem>
                                    <SelectItem value="3">Grade 3</SelectItem>
                                    <SelectItem value="4">Grade 4</SelectItem>
                                    <SelectItem value="5">Grade 5</SelectItem>
                                    <SelectItem value="6">Grade 6</SelectItem>
                                    <SelectItem value="7">Grade 7</SelectItem>
                                    <SelectItem value="8">Grade 8</SelectItem>
                                  </SelectContent>
                                </Select>
                                <FormMessage className="text-destructive" />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="academicYear"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Academic Year</FormLabel>
                                <FormControl>
                                  <Input placeholder="E.g., 2083" {...field} />
                                </FormControl>
                                <FormMessage className="text-destructive" />
                              </FormItem>
                            )}
                          />
                        </motion.div>

                        <FormField
                          control={form.control}
                          name="hasSiblings"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                Does the student have any siblings currently
                                enrolled in our school?
                              </FormLabel>
                              <RadioGroup
                                defaultValue={field.value}
                                onValueChange={field.onChange}
                                className="flex flex-wrap gap-4"
                              >
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem
                                    value="yes"
                                    id="yes-sibling"
                                  />
                                  <Label htmlFor="yes-sibling">Yes</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="no" id="no-sibling" />
                                  <Label htmlFor="no-sibling">No</Label>
                                </div>
                              </RadioGroup>
                              <FormMessage className="text-destructive" />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="healthInfo"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                Health Information (allergies, medical
                                conditions, etc.)
                              </FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder="Please provide any relevant health information"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage className="text-destructive" />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="additionalInfo"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Additional Information</FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder="Please provide any additional information that may be relevant to the application"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage className="text-destructive" />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="terms"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                />
                              </FormControl>
                              <div className="space-y-1 leading-none">
                                <FormLabel>
                                  I confirm that all information provided is
                                  accurate and complete. I understand that
                                  providing false information may result in the
                                  cancellation of the application or admission.
                                </FormLabel>
                                <FormMessage className="text-destructive" />
                              </div>
                            </FormItem>
                          )}
                        />
                      </motion.div>
                    )}

                    <div className="mt-8 flex justify-between items-center">
                      {step > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handlePrevStep}
                          disabled={loading}
                        >
                          <ArrowLeft className="h-4 w-4 mr-2" />
                          Previous
                        </Button>
                      )}
                      {step === 1 && <div></div>}

                      <div className="flex flex-col items-end">
                        <Button
                          type="button"
                          disabled={loading}
                          onClick={async () => {
                            try {
                              await handleNextStep(form.getValues());
                            } catch (error) {
                              console.error("Error:", error);
                            }
                          }}
                          className="min-w-[140px]"
                        >
                          {step < 3 ? (
                            "Next Step"
                          ) : loading ? (
                            <Loader2 className="animate-spin h-4 w-4" />
                          ) : (
                            <>
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              Submit Application
                            </>
                          )}
                        </Button>

                        {/* Show validation hint */}
                        {Object.keys(form.formState.errors).length > 0 && (
                          <p className="text-sm text-destructive mt-2 max-w-[200px] text-right">
                            Please fill all required fields correctly
                          </p>
                        )}
                      </div>
                    </div>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
